import type { NavTreeItem } from '@models/NavigationTree';
import type { LocalesUnion } from '@constants/i18n';
import { LOCALES, LOCALES_ARR } from '@constants/i18n';
import {
  API_FOLDER,
  INDEX_FILE_NAME,
  LOCALIZATION_PATH,
  MDX_EXT,
  SOURCE_PATH,
  SOURCE_API_PATH,
  LOCALIZATION_API_PATHS,
} from '@constants/docs';

export function removeFileExtension(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '');
}

export const removeIndexFilename = (path: string): string => {
  const regex = new RegExp(`(^|/)${INDEX_FILE_NAME}${MDX_EXT}$`);
  return path.replace(regex, '');
};

// should be removed, dependents (getHrefFromPath) should be refactored
const removeIndexAndExtensionFromPath = (path: string): string => removeFileExtension(removeIndexFilename(path));

const removeRootPartFromDocPath = (path: string): string =>
  path.replace(/(source\/?)|(localization\/(ja|ko|zh-Hans)\/?)/, '');

export const isApiPage = (pageFullPath: string): boolean =>
  [SOURCE_API_PATH, ...LOCALIZATION_API_PATHS].some((basePath) => pageFullPath.startsWith(basePath));

export const zipObject = (keys: string[], values: any[]) =>
  keys.reduce((obj, k, i) => ({ ...obj, [k]: values[i] }), {});

export const getHrefFromPath = (path: string): string =>
  removeIndexAndExtensionFromPath(removeRootPartFromDocPath(path));

export const linkToId = (link: string): string => link.slice(1);

export const isTruncatedLink = (item: NavTreeItem) => !`${item?.title}`.includes(' ') && `${item?.title}`.length > 25;

export const getUrlPathFromSlug = (slug?: string[] | string): string => {
  const rawUrlPath = Array.isArray(slug) ? slug.join('/') : slug || '';

  return rawUrlPath.replace(new RegExp(`(^|/)${INDEX_FILE_NAME}$`), '');
};

export const getFlattenedPath = (urlPath: string, locale: LocalesUnion): string => {
  const urlPathSegments = urlPath
    .replace(new RegExp(`${INDEX_FILE_NAME}$`), '')
    .split('/')
    .filter((segment) => segment !== '');

  // API reference pages are not localized, always use source path
  const isApiRefPath = urlPathSegments[0] === API_FOLDER;

  if (locale === LOCALES.EN_US || isApiRefPath) {
    return [SOURCE_PATH, ...urlPathSegments].join('/');
  }

  return [LOCALIZATION_PATH, locale, ...urlPathSegments].join('/');
};

export const getFallbackFlattenedPath = (flattenedPath: string): string => {
  const localizationPattern = new RegExp(`^${LOCALIZATION_PATH}/(?:${LOCALES_ARR.join('|')})`);

  return flattenedPath.replace(localizationPattern, SOURCE_PATH);
};

export const getFlattenedPathFromSourceFilePath = (sourceFilePath: string): string =>
  removeFileExtension(sourceFilePath)
    .replace(new RegExp(`${INDEX_FILE_NAME}$`), '')
    .replace(/\/$/, '');
